import { playerNameFor } from '@/composables/api/usePlayerSummaries';
import SubscriberZoneIcon from '@/assets/icons/models/model-location-zone-subscriber.svg?component';
import { peekZoneSummary, zoneSummaryFor, type ZoneLookup } from '@/composables/api/useZoneSummaries';
import { scheduleNow } from '@/composables/useScheduleClock';
import { zoneWallClock } from '@/composables/api/useTimeZones';
import { formatCount } from '@/utils/formatCount';
import {
    scheduleRole,
    summarizeSchedule,
    summaryText,
    type ScheduleRole,
    type ScheduleRow,
} from '@/utils/customPlaylistSchedule';
import type { ArrayColumnBadge } from './index';

/**
 * The custom-list Schedules cell (decision D1). The table row is a
 * LocationZoneCustomPlaylist search document; each nested schedule carries its
 * own `location_zone_id`, and the list carries its owner's `location_zone_id`.
 *
 * - A filled count for schedules written by the list's own zone.
 * - An outlined count with the subscriber-zone icon for schedules written by
 *   subscribers of that zone.
 * - A destructive count, drawn only when it is not zero, for schedules on any
 *   other zone. See scheduleBadges.
 */

/** Joined text and tooltip stop listing after this many distinct lines. */
export const MAX_SUMMARY_LINES = 20;

function asScheduleRows(items: Record<string, unknown>[]): ScheduleRow[] {
    return items.filter((item) => !!item && typeof item === 'object') as ScheduleRow[];
}

/**
 * Whether a nested schedule carries its own columns yet. A list document
 * indexed before those fields shipped holds only `{id, display_string}` per
 * schedule, until the post-deploy `scout:import` (or a later save) rewrites it.
 * Such an item says nothing about its zone, type or times, so it is counted
 * nowhere and never drawn as if it were a schedule.
 */
export function isIndexedSchedule(item: unknown): boolean {
    return !!item && typeof item === 'object' && (item as ScheduleRow).custom_playlist_schedule_type_id != null;
}

/** What a list shows while every one of its schedules is still in the old shape. */
export const SCHEDULES_UPDATING_TEXT = 'Schedules are updating';

function ownerZoneId(row: Record<string, unknown>): number | null {
    const id = Number(row.location_zone_id);
    return Number.isInteger(id) && id > 0 ? id : null;
}

function isOwnerRow(schedule: ScheduleRow, ownerId: number | null): boolean {
    return ownerId !== null && schedule.location_zone_id != null && Number(schedule.location_zone_id) === ownerId;
}

function masterIdFrom(lookup: ZoneLookup): number | null | undefined {
    // A zone the lookup could not find (deleted, or hidden from this user) has
    // no known master. It stays unplaced (undefined), never "subscriber" or
    // "other zone" (amendment A8), and scheduleBadges counts it in no box.
    if (lookup === undefined || lookup === null) return undefined;
    return lookup.masterLocationZoneId;
}

/**
 * Owner, subscriber or other for each schedule. Only a row that has schedules
 * from another zone asks the zone lookup. It asks for the owner zone in the same
 * tick, so the lookup fetches it in the same request as the other zones rather
 * than in a second one when the card reads "now" on the owner's clock.
 */
export function scheduleRoles(items: Record<string, unknown>[], row: Record<string, unknown>): (ScheduleRole | null)[] {
    const ownerId = ownerZoneId(row);
    const schedules = asScheduleRows(items);
    const hasCrossZone = schedules.some((schedule) => !isOwnerRow(schedule, ownerId));
    if (hasCrossZone && ownerId !== null) {
        zoneSummaryFor(ownerId);
    }
    return schedules.map((schedule) => {
        if (isOwnerRow(schedule, ownerId)) return 'owner';
        const lookup = schedule.location_zone_id != null ? zoneSummaryFor(schedule.location_zone_id) : null;
        return scheduleRole(schedule, { ownerZoneId: ownerId, zoneMasterId: masterIdFrom(lookup) });
    });
}

/** "1 schedule", "2 schedules". */
function schedulesCount(count: number): string {
    return `${formatCount(count)} ${count === 1 ? 'schedule' : 'schedules'}`;
}

/**
 * The counts for a list's schedules. Every schedule counts, whether it is
 * active, upcoming or expired, because operators update expired schedules as
 * part of normal work and the card lists them under the counts (SAU-537). So
 * the counts do not depend on the time, and each label says "active or not".
 *
 * - "here" and "subscriber" are always drawn, as 0 when empty, so the counts
 *   keep their places from one list to the next.
 * - "problem" counts schedules on a zone that is neither the list's zone nor
 *   one of its subscribers. That should never happen, so it is drawn only when
 *   it does, and in the destructive colour. An expired one still counts: an
 *   operator who re-dates it would make it play on that zone.
 * - A schedule whose zone lookup has not answered yet is counted nowhere until
 *   it does, so the problem count never flashes up while the page loads.
 */
export function scheduleBadges(items: Record<string, unknown>[], row: Record<string, unknown>): ArrayColumnBadge[] {
    const schedules = asScheduleRows(items).filter(isIndexedSchedule);
    const roles = scheduleRoles(schedules as Record<string, unknown>[], row);
    let here = 0;
    let subscriber = 0;
    let problem = 0;
    roles.forEach((role) => {
        // Only a known zone that is neither this list's nor a subscriber is a
        // problem. A zone the lookup has not answered for, could not find (a
        // deleted zone, or another client's zone a restricted user cannot see),
        // or a schedule with no zone at all is unknown, and counts nowhere.
        if (role === 'owner') here += 1;
        else if (role === 'subscriber') subscriber += 1;
        else if (role === 'other') problem += 1;
    });
    return [
        {
            key: 'here',
            count: here,
            variant: 'filled',
            alwaysShow: true,
            title: "Scheduled by this list's own zone",
            label: `${schedulesCount(here)} from this list's own zone, active or not`,
        },
        {
            key: 'subscriber',
            count: subscriber,
            variant: 'outlined',
            alwaysShow: true,
            icon: SubscriberZoneIcon,
            title: 'Scheduled by subscriber zones',
            label: `${schedulesCount(subscriber)} from ${subscriber === 1 ? 'a subscriber zone' : 'subscriber zones'}, active or not`,
        },
        {
            key: 'problem',
            count: problem,
            variant: 'problem',
            title: "On a zone that is neither this list's zone nor one of its subscribers",
            label: `${schedulesCount(problem)}, active or not, on ${problem === 1 ? 'a zone that is' : 'zones that are'} neither this list's zone nor one of its subscribers. This needs fixing.`,
        },
    ];
}

/**
 * How many zones subscribe to a zone, as a borderless count with the
 * subscriber mark: the number at the end of a zone line, on the custom-list
 * card and on each zone heading in the Schedules view. It counts zones, not
 * schedules, so it stays put: a zone with no subscribers says 0. Empty until
 * the zone lookup answers (one batched request for the page), or when the zone
 * cannot be found.
 */
export function subscriberZoneBadges(zoneId: unknown): ArrayColumnBadge[] {
    const zone = zoneSummaryFor(zoneId);
    if (!zone) return [];
    const count = zone.subscribersCount ?? 0;
    return [
        {
            key: 'subscriber-zones',
            count,
            variant: 'outlined',
            alwaysShow: true,
            icon: SubscriberZoneIcon,
            label: `${formatCount(count)} ${count === 1 ? 'zone subscribes' : 'zones subscribe'} to this zone`,
        },
    ];
}

/**
 * "Now" on the owner zone's clock, or the browser's clock until the zone's
 * time zone is known (amendment A6). It asks the lookup for the owner zone, so
 * the table cell, the card line and the drawer all use the same clock. A page
 * of rows still makes one batched request. Call it for a list that has
 * schedules, or for a schedule's own search record. A schedule record's
 * `location_zone_id` is the schedule's own zone, which is the clock it plays on.
 */
export function ownerZoneNow(row: Record<string, unknown>, instant: Date = scheduleNow()): Date {
    return zoneWallClock(instant, zoneSummaryFor(row.location_zone_id)) ?? new Date(instant.getTime());
}

/**
 * "Now" on the clock of the zone whose player plays this schedule. Schedule
 * dates and times are naive values on that zone's clock, and a subscriber in
 * another time zone plays its own schedules on its own clock. Falls back to
 * the owner zone's clock, then the browser's, while the zone is unknown. Only
 * peeks: whoever groups or counts the schedules has already asked for the zone.
 */
export function scheduleZoneNow(schedule: ScheduleRow, row: Record<string, unknown>, instant: Date = scheduleNow()): Date {
    const lookup = schedule.location_zone_id != null ? peekZoneSummary(schedule.location_zone_id) : null;
    return zoneWallClock(instant, lookup) ?? ownerZoneNow(row, instant);
}

/** "text" for one, "text ×3" for three identical lines. */
function countedLines(lines: string[]): string[] {
    const counts = new Map<string, number>();
    for (const line of lines) {
        counts.set(line, (counts.get(line) ?? 0) + 1);
    }
    return [...counts].map(([line, total]) => (total > 1 ? `${line} ×${total}` : line));
}

/**
 * The plain-text lines behind the joined cell text and its tooltip: every
 * schedule active now, the owner zone's first, identical lines folded into
 * one with a count, and at most MAX_SUMMARY_LINES of them. A list whose
 * schedules are all upcoming or expired says so instead of going blank.
 */
export function scheduleItemSummaries(
    items: Record<string, unknown>[],
    row: Record<string, unknown>,
    instant: Date = scheduleNow(),
): string[] {
    const ownerId = ownerZoneId(row);
    const all = asScheduleRows(items);
    if (all.length === 0) {
        return [];
    }
    const schedules = all.filter(isIndexedSchedule);
    if (schedules.length === 0) {
        return [SCHEDULES_UPDATING_TEXT];
    }
    const summaries = schedules.map((schedule) => ({
        owner: isOwnerRow(schedule, ownerId),
        summary: summarizeSchedule(schedule, { now: scheduleZoneNow(schedule, row, instant), playerName: playerNameFor }),
    }));

    const inForce = summaries.filter(({ summary }) => summary.state === 'in-force');
    if (inForce.length === 0) {
        const upcoming = summaries.filter(({ summary }) => summary.state === 'upcoming').length;
        const expired = summaries.length - upcoming;
        const counts = [upcoming > 0 ? `${upcoming} upcoming` : null, expired > 0 ? `${expired} expired` : null]
            .filter(Boolean)
            .join(', ');
        return [`None active (${counts})`];
    }

    // Folded within each group, so the owner's own line never absorbs another zone's count.
    const lines = [
        ...countedLines(inForce.filter(({ owner }) => owner).map(({ summary }) => summaryText(summary))),
        ...countedLines(inForce.filter(({ owner }) => !owner).map(({ summary }) => summaryText(summary))),
    ];
    if (lines.length <= MAX_SUMMARY_LINES) {
        return lines;
    }
    return [...lines.slice(0, MAX_SUMMARY_LINES), `+${lines.length - MAX_SUMMARY_LINES} more`];
}
